import React, { useState, useRef, useEffect } from 'react';
import { usePermissions } from 'react-admin';
import {
    Paper, IconButton, TextField, Typography, CircularProgress, Fab,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import ChatIcon from '@material-ui/icons/QuestionAnswer';

// Chat gateway (lams-agent). The gateway enforces auth and role scoping
// server-side; this panel only renders the conversation.
const AGENT_URL = process.env.REACT_APP_AGENT_URL || 'https://dev.lamsaz.com:8093';
const ALLOWED_ROLES = ['admin', 'officemgr', 'teacher', 'toddlerteacher', 'toddlerassistant'];

const styles = {
    fab: { position: 'fixed', bottom: 24, right: 24, zIndex: 1300 },
    panel: {
        position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
        width: 380, height: 520, display: 'flex', flexDirection: 'column',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', borderBottom: '1px solid #e0e0e0',
    },
    messages: { flex: 1, overflowY: 'auto', padding: 12 },
    bubbleUser: {
        background: '#1976d2', color: '#fff', borderRadius: 12,
        padding: '8px 12px', margin: '4px 0 4px 48px', whiteSpace: 'pre-wrap',
    },
    bubbleAgent: {
        background: '#f0f0f0', borderRadius: 12,
        padding: '8px 12px', margin: '4px 48px 4px 0', whiteSpace: 'pre-wrap',
    },
    bubbleTool: { color: '#888', fontStyle: 'italic', margin: '2px 0', fontSize: 12 },
    inputRow: { display: 'flex', padding: 8, borderTop: '1px solid #e0e0e0' },
};

const AgentChat = () => {
    const { permissions } = usePermissions();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]); // {kind: 'user'|'agent'|'tool'|'error', text}
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages, open]);

    if (!permissions || !ALLOWED_ROLES.includes(permissions.role)) return null;

    const send = async () => {
        const text = input.trim();
        if (!text || busy) return;
        setInput('');
        setMessages((m) => [...m, { kind: 'user', text }]);
        setBusy(true);
        try {
            const resp = await fetch(AGENT_URL + '/agent/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ conversation_id: 'default', message: text }),
            });
            if (!resp.ok) {
                const errText = await resp.text();
                setMessages((m) => [...m, { kind: 'error', text: `Request failed (${resp.status}): ${errText}` }]);
                return;
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split('\n\n');
                buffer = parts.pop();
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith('data: ')) continue;
                    const event = JSON.parse(line.slice(6));
                    if (event.type === 'tool') {
                        setMessages((m) => [...m, { kind: 'tool', text: `Looking up ${event.tool.replace(/_/g, ' ')}...` }]);
                    } else if (event.type === 'status') {
                        setMessages((m) => [...m, { kind: 'tool', text: event.text }]);
                    } else if (event.type === 'message') {
                        setMessages((m) => [...m, { kind: 'agent', text: event.text }]);
                    } else if (event.type === 'error') {
                        setMessages((m) => [...m, { kind: 'error', text: event.text }]);
                    }
                }
            }
        } catch (e) {
            setMessages((m) => [...m, { kind: 'error', text: `Could not reach the assistant: ${e.message}` }]);
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <Fab color="primary" style={styles.fab} onClick={() => setOpen(true)} aria-label="Open LAMS assistant">
                <ChatIcon />
            </Fab>
        );
    }

    return (
        <Paper elevation={6} style={styles.panel}>
            <div style={styles.header}>
                <Typography variant="subtitle1">LAMS Assistant</Typography>
                <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close assistant">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>
            <div style={styles.messages}>
                {messages.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        Ask about students, attendance, absences, balances or school stats.
                    </Typography>
                )}
                {messages.map((m, i) => {
                    if (m.kind === 'tool') return <div key={i} style={styles.bubbleTool}>{m.text}</div>;
                    if (m.kind === 'error') return <div key={i} style={{ ...styles.bubbleAgent, color: '#b00020' }}>{m.text}</div>;
                    return <div key={i} style={m.kind === 'user' ? styles.bubbleUser : styles.bubbleAgent}>{m.text}</div>;
                })}
                {busy && <CircularProgress size={18} style={{ margin: 8 }} />}
                <div ref={bottomRef} />
            </div>
            <div style={styles.inputRow}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Ask a question..."
                    value={input}
                    disabled={busy}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                    inputProps={{ 'aria-label': 'Message the LAMS assistant' }}
                />
                <IconButton color="primary" onClick={send} disabled={busy || !input.trim()} aria-label="Send">
                    <SendIcon />
                </IconButton>
            </div>
        </Paper>
    );
};

export default AgentChat;
